import { useEffect, useRef, useState, useCallback } from 'react'
import mapboxgl from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'
import axios from 'axios'
import api from '../utils/api'
import { vesselGroups } from '../data/vesselGroups'
import { spotterFleet } from '../data/spotterPlanes'

mapboxgl.accessToken = 'pk.eyJ1IjoiZm9yYWdlZmlzaCIsImEiOiJjbTZreHgzNHIwMG8xMmpxeHE3OWNjemJlIn0.Gy8UzHPahz3h6tGKsNMqJQ'

const GFW_API_BASE = 'https://gateway.api.globalfishingwatch.org'
const GFW_TOKEN = import.meta.env.VITE_GFW_API_TOKEN

const BURGUNDY = '#8B1A1A'
const GOLD = '#B8860B'
const BLUE = '#4a9eff'
const GREEN = '#10b981'
const PLANE_COLOR = '#a78bfa' // purple — distinct from vessel colors

// NOAA CoastWatch ERDDAP SST tile URL (free, no auth required)
// erdMH1sstd1day: MODIS Aqua daily SST, 0.01 degree resolution
const SST_TILE_URL = 'https://coastwatch.pfeg.noaa.gov/erddap/griddap/erdMH1sstd1day.png?SST%5B(last)%5D%5B(0.0)%5D%5B(18.0):(50.0)%5D%5B(-98.0):(-60.0)%5D&.draw=surface&.vars=longitude%7Clatitude%7CSST&.colorBar=%7C%7C%7C0%7C30%7C&.bgColor=0x00000000'

const S = {
  shell: { background: '#0a0a0a', color: '#e0e0e0', minHeight: '100%', fontFamily: "'DM Sans', sans-serif" },
  statsRow: { display: 'flex', gap: '1rem', marginBottom: '1.25rem', flexWrap: 'wrap' },
  statCard: { background: '#111', border: '1px solid #1e1e1e', borderRadius: '8px', padding: '1rem 1.5rem', flex: '1', minWidth: '130px' },
  statValue: { fontSize: '1.6rem', fontWeight: 700, color: GOLD, lineHeight: 1 },
  statLabel: { fontSize: '0.72rem', color: '#bbb', marginTop: '0.3rem', textTransform: 'uppercase', letterSpacing: '0.08em' },
  layersRow: { display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'center' },
  layerToggle: {
    display: 'flex', alignItems: 'center', gap: '0.45rem',
    padding: '0.45rem 0.85rem', borderRadius: '5px', border: '1px solid',
    fontSize: '0.8rem', cursor: 'pointer', fontWeight: 500,
    transition: 'opacity 0.15s', background: 'transparent',
  },
  dot: { width: '9px', height: '9px', borderRadius: '50%', flexShrink: 0 },
  mapWrapper: { borderRadius: '8px', overflow: 'hidden', border: '1px solid #1e1e1e', position: 'relative' },
  loadingOverlay: {
    position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10,
  },
  loadingBox: { background: '#111', border: '1px solid #2a2a2a', borderRadius: '8px', padding: '1.5rem 2rem', textAlign: 'center' },
  spinner: { width: '36px', height: '36px', border: '3px solid #1e1e1e', borderTop: `3px solid ${GOLD}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 0.75rem' },
  legend: {
    position: 'absolute', bottom: '2.5rem', left: '0.75rem', zIndex: 5,
    background: 'rgba(10,10,10,0.85)', border: '1px solid #2a2a2a',
    borderRadius: '6px', padding: '0.65rem 0.85rem', fontSize: '0.72rem',
  },
  legendRow: { display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.3rem', color: '#ccc' },
  modal: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '1rem',
  },
  modalBox: { background: '#111', border: '1px solid #2a2a2a', borderRadius: '10px', maxWidth: '440px', width: '100%', maxHeight: '80vh', overflowY: 'auto' },
  modalHeader: { padding: '1.1rem 1.4rem', borderBottom: '1px solid #1e1e1e', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' },
  modalTitle: { fontSize: '1rem', fontWeight: 700, color: '#e0e0e0', margin: 0 },
  modalSub: { fontSize: '0.75rem', color: '#999', marginTop: '0.15rem' },
  modalBody: { padding: '1.1rem 1.4rem' },
  closeBtn: { background: 'none', border: 'none', color: '#888', cursor: 'pointer', fontSize: '1.2rem', lineHeight: 1, padding: '0.1rem' },
  fieldLabel: { fontSize: '0.7rem', color: '#bbb', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.15rem' },
  fieldValue: { fontSize: '0.88rem', color: '#ccc', fontFamily: 'monospace', marginBottom: '0.75rem' },
  orbitBadge: { display: 'inline-flex', alignItems: 'center', gap: '0.35rem', padding: '0.25rem 0.6rem', borderRadius: '4px', fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' },
  sourceNote: { fontSize: '0.72rem', color: '#888', marginTop: '0.75rem' },
}

// Detect if a plane position is close to any vessel (within ~15 nautical miles)
function findNearbyVessels(planeLat, planeLng, vesselPositions, thresholdDeg = 0.25) {
  return vesselPositions.filter(v => {
    const dlat = Math.abs(v.lat - planeLat)
    const dlng = Math.abs(v.lng - planeLng)
    return dlat < thresholdDeg && dlng < thresholdDeg
  })
}

export default function ActivityMap() {
  const mapContainer = useRef(null)
  const map = useRef(null)
  const vesselMarkers = useRef([])
  const planeMarkers = useRef([])

  const [layers, setLayers] = useState({ vessels: true, planes: true, sst: false })
  const [vesselPositions, setVesselPositions] = useState([])
  const [planePositions, setPlanePositions] = useState([])
  const [loadingVessels, setLoadingVessels] = useState(true)
  const [loadingPlanes, setLoadingPlanes] = useState(true)
  const [selected, setSelected] = useState(null) // { type: 'vessel'|'plane', data: {...} }
  const [sstLoaded, setSstLoaded] = useState(false)
  const [planeSource, setPlaneSource] = useState(null) // 'flightaware' | 'no-key' | 'simulated'

  const toggleLayer = (key) => setLayers(prev => ({ ...prev, [key]: !prev[key] }))

  // Init map
  useEffect(() => {
    if (map.current) return
    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/dark-v11',
      center: [-77, 36.5],
      zoom: 5,
      projection: 'mercator',
    })
    map.current.addControl(new mapboxgl.NavigationControl(), 'top-right')
    map.current.addControl(new mapboxgl.ScaleControl(), 'bottom-left')
    return () => {
      if (map.current) { map.current.remove(); map.current = null }
    }
  }, [])

  // SST overlay — add/remove raster image layer
  useEffect(() => {
    if (!map.current) return
    const onLoad = () => {
      if (layers.sst && !map.current.getSource('sst')) {
        // Use NOAA CoastWatch WMS — simpler than ERDDAP tile format
        map.current.addSource('sst', {
          type: 'raster',
          tiles: [
            'https://coastwatch.pfeg.noaa.gov/erddap/wms/erdMH1sstd1day/request?SERVICE=WMS&VERSION=1.3.0&REQUEST=GetMap&BBOX={bbox-epsg-3857}&CRS=EPSG:3857&WIDTH=256&HEIGHT=256&LAYERS=erdMH1sstd1day:SST&STYLES=&FORMAT=image/png&TRANSPARENT=TRUE&colorscalerange=0,30',
          ],
          tileSize: 256,
          attribution: 'NOAA CoastWatch / MODIS Aqua SST',
        })
        map.current.addLayer({
          id: 'sst-layer',
          type: 'raster',
          source: 'sst',
          paint: { 'raster-opacity': 0.6 },
        }, 'water-shadow') // insert below vessel markers
        setSstLoaded(true)
      } else if (!layers.sst && map.current.getLayer('sst-layer')) {
        map.current.removeLayer('sst-layer')
        map.current.removeSource('sst')
        setSstLoaded(false)
      }
    }

    if (map.current.loaded()) {
      onLoad()
    } else {
      map.current.once('load', onLoad)
    }
  }, [layers.sst])

  // Fetch vessel positions from GFW
  useEffect(() => {
    const fetchVessels = async () => {
      setLoadingVessels(true)
      const allVessels = Object.values(vesselGroups).flatMap(g =>
        g.vessels.map(v => ({ ...v, groupId: g.id, groupColor: g.color, groupName: g.name }))
      )
      const endDate = new Date().toISOString().split('T')[0]
      const startDate = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
      const positions = []
      const batchSize = 5

      for (let i = 0; i < allVessels.length; i += batchSize) {
        const batch = allVessels.slice(i, i + batchSize)
        const results = await Promise.all(batch.map(async vessel => {
          try {
            const searchRes = await axios.get(`${GFW_API_BASE}/v3/vessels/search`, {
              params: { query: vessel.mmsi, 'datasets[0]': 'public-global-vessel-identity:latest' },
              headers: { Authorization: `Bearer ${GFW_TOKEN}` },
            })
            if (!searchRes.data?.entries?.length) return fallbackVessel(vessel)
            const vesselId = searchRes.data.entries[0].id
            const eventsRes = await axios.get(`${GFW_API_BASE}/v3/events`, {
              params: {
                'vessels[0]': vesselId,
                'datasets[0]': 'public-global-fishing-events:latest',
                'start-date': startDate, 'end-date': endDate,
              },
              headers: { Authorization: `Bearer ${GFW_TOKEN}` },
            })
            if (eventsRes.data?.entries?.length > 0) {
              const ev = eventsRes.data.entries[0]
              if (ev.position?.lat && ev.position?.lon) {
                return { ...vessel, lat: ev.position.lat, lng: ev.position.lon, lastSeen: new Date(ev.start), dataSource: 'gfw-api' }
              }
            }
            return fallbackVessel(vessel)
          } catch {
            return fallbackVessel(vessel)
          }
        }))
        positions.push(...results.filter(Boolean))
        if (i + batchSize < allVessels.length) await new Promise(r => setTimeout(r, 500))
      }
      setVesselPositions(positions)
      setLoadingVessels(false)
    }
    fetchVessels()
  }, [])

  // Fetch plane positions from backend proxy
  useEffect(() => {
    const fetchPlanes = async () => {
      setLoadingPlanes(true)
      try {
        const res = await api.get('/data/planes')
        setPlaneSource(res.data.source)
        if (res.data.source === 'no-key' || !res.data.planes?.length) {
          // No API key — simulate positions near Reedville for the active season
          setPlanePositions(simulatePlanePositions())
          setPlaneSource('simulated')
        } else {
          setPlanePositions(res.data.planes)
        }
      } catch {
        setPlanePositions(simulatePlanePositions())
        setPlaneSource('simulated')
      }
      setLoadingPlanes(false)
    }
    fetchPlanes()
  }, [])

  // Render vessel markers
  useEffect(() => {
    if (!map.current || !vesselPositions.length) return
    vesselMarkers.current.forEach(m => m.remove())
    vesselMarkers.current = []
    if (!layers.vessels) return

    vesselPositions.forEach(vessel => {
      const el = document.createElement('div')
      el.style.cssText = `width:18px;height:18px;border-radius:50%;background:${vessel.groupColor};border:2px solid rgba(255,255,255,0.5);box-shadow:0 2px 6px rgba(0,0,0,0.5);cursor:pointer;`
      el.addEventListener('click', () => setSelected({ type: 'vessel', data: vessel }))
      const popup = new mapboxgl.Popup({ offset: 20, closeButton: false })
        .setHTML(`<div style="padding:6px;font-family:DM Sans,sans-serif;min-width:160px;">
          <div style="font-weight:700;font-size:12px;color:${vessel.groupColor};margin-bottom:2px;">${vessel.name}</div>
          <div style="font-size:10px;color:#999;">${vessel.groupName}</div>
          <div style="font-size:10px;color:#bbb;margin-top:4px;">MMSI: ${vessel.mmsi}</div>
          <div style="font-size:10px;color:${vessel.dataSource === 'gfw-api' ? '#10b981' : '#B8860B'};margin-top:3px;">
            ${vessel.dataSource === 'gfw-api' ? '✓ Real AIS data' : '△ Simulated position'}
          </div>
        </div>`)
      const marker = new mapboxgl.Marker(el).setLngLat([vessel.lng, vessel.lat]).setPopup(popup).addTo(map.current)
      vesselMarkers.current.push(marker)
    })
  }, [vesselPositions, layers.vessels])

  // Render plane markers
  useEffect(() => {
    if (!map.current) return
    planeMarkers.current.forEach(m => m.remove())
    planeMarkers.current = []
    if (!layers.planes || !planePositions.length) return

    planePositions.forEach(plane => {
      const nearby = findNearbyVessels(plane.lat, plane.lng, vesselPositions)
      const isOrbiting = nearby.length > 0 && plane.active

      const el = document.createElement('div')
      const size = isOrbiting ? '22px' : '16px'
      el.style.cssText = `
        width:${size};height:${size};
        background:${isOrbiting ? BURGUNDY : PLANE_COLOR};
        border:2px solid rgba(255,255,255,${isOrbiting ? '0.9' : '0.5'});
        border-radius:3px;
        transform:rotate(${plane.heading || 0}deg);
        cursor:pointer;
        box-shadow:${isOrbiting ? `0 0 10px ${BURGUNDY}90` : '0 2px 5px rgba(0,0,0,0.4)'};
        transition:box-shadow 0.2s;
      `
      if (isOrbiting) {
        el.title = 'ACTIVE — near vessel'
      }
      el.addEventListener('click', () => setSelected({ type: 'plane', data: { ...plane, nearby, isOrbiting } }))

      const popup = new mapboxgl.Popup({ offset: 20, closeButton: false })
        .setHTML(`<div style="padding:6px;font-family:DM Sans,sans-serif;min-width:160px;">
          <div style="font-weight:700;font-size:12px;color:${isOrbiting ? BURGUNDY : PLANE_COLOR};margin-bottom:2px;">${plane.ident}</div>
          <div style="font-size:10px;color:#999;">Alpha VesselCo / Omega Protein</div>
          ${plane.altitude != null ? `<div style="font-size:10px;color:#ccc;margin-top:3px;">Alt: ${plane.altitude} ft · ${plane.groundspeed || '?'} kts</div>` : ''}
          ${isOrbiting ? `<div style="font-size:10px;color:${BURGUNDY};font-weight:700;margin-top:4px;">⚠ NEAR VESSEL — possible active fishing</div>` : ''}
          ${planeSource === 'simulated' ? '<div style="font-size:9px;color:#666;margin-top:3px;">△ Simulated — add FlightAware key for live data</div>' : ''}
        </div>`)

      const marker = new mapboxgl.Marker(el).setLngLat([plane.lng, plane.lat]).setPopup(popup).addTo(map.current)
      planeMarkers.current.push(marker)
    })
  }, [planePositions, layers.planes, vesselPositions, planeSource])

  const loading = loadingVessels || loadingPlanes

  const activePlanes = planePositions.filter(p => p.active).length
  const orbitingPlanes = planePositions.filter(p => p.active && findNearbyVessels(p.lat, p.lng, vesselPositions).length > 0).length
  const totalVessels = Object.values(vesselGroups).reduce((n, g) => n + g.vessels.length, 0)

  return (
    <div style={S.shell}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      <div style={S.statsRow}>
        <div style={S.statCard}>
          <div style={S.statValue}>{totalVessels}</div>
          <div style={S.statLabel}>Vessels Tracked</div>
        </div>
        <div style={S.statCard}>
          <div style={S.statValue}>{spotterFleet.length}</div>
          <div style={S.statLabel}>Spotter Aircraft</div>
        </div>
        <div style={S.statCard}>
          <div style={{ ...S.statValue, color: activePlanes > 0 ? BURGUNDY : '#666' }}>{activePlanes}</div>
          <div style={S.statLabel}>Planes Airborne</div>
        </div>
        <div style={S.statCard}>
          <div style={{ ...S.statValue, color: orbitingPlanes > 0 ? BURGUNDY : '#666' }}>{orbitingPlanes}</div>
          <div style={S.statLabel}>Near Vessels</div>
        </div>
        <div style={{ ...S.statCard, flex: 2 }}>
          <div style={{ fontSize: '0.78rem', color: '#bbb', lineHeight: 1.5 }}>
            {planeSource === 'flightaware'
              ? <span style={{ color: GREEN }}>Live FlightAware data</span>
              : planeSource === 'simulated'
              ? <span style={{ color: GOLD }}>Simulated plane positions — add <code style={{ fontSize: '0.75rem' }}>FLIGHTAWARE_API_KEY</code> for live tracking</span>
              : <span style={{ color: '#888' }}>Loading...</span>
            }
            {' · '}
            {GFW_TOKEN ? <span style={{ color: GREEN }}>GFW live vessel data</span> : <span style={{ color: '#888' }}>Vessel positions simulated</span>}
          </div>
        </div>
      </div>

      <div style={S.layersRow}>
        {[
          { key: 'vessels', label: 'Fishing Vessels', color: '#EF4444' },
          { key: 'planes', label: 'Spotter Planes', color: PLANE_COLOR },
          { key: 'sst', label: 'Sea Surface Temp', color: BLUE },
        ].map(({ key, label, color }) => (
          <button
            key={key}
            onClick={() => toggleLayer(key)}
            style={{
              ...S.layerToggle,
              borderColor: layers[key] ? color : '#2a2a2a',
              color: layers[key] ? '#e0e0e0' : '#888',
              opacity: layers[key] ? 1 : 0.6,
              background: layers[key] ? `${color}12` : 'transparent',
            }}
          >
            <span style={{ ...S.dot, background: color, opacity: layers[key] ? 1 : 0.3 }} />
            {label}
          </button>
        ))}
        <div style={{ marginLeft: 'auto', fontSize: '0.72rem', color: '#888', fontStyle: 'italic' }}>
          Square markers = planes · Round = vessels · Red square = plane near vessel
        </div>
      </div>

      <div style={S.mapWrapper}>
        <div ref={mapContainer} style={{ width: '100%', height: '620px' }} />

        {loading && (
          <div style={S.loadingOverlay}>
            <div style={S.loadingBox}>
              <div style={S.spinner} />
              <div style={{ fontSize: '0.88rem', color: '#ccc' }}>Loading activity data...</div>
              <div style={{ fontSize: '0.75rem', color: '#bbb', marginTop: '0.25rem' }}>
                {loadingVessels && 'Querying GFW vessel positions'}
                {loadingVessels && loadingPlanes && ' · '}
                {loadingPlanes && 'Fetching spotter plane locations'}
              </div>
            </div>
          </div>
        )}

        <div style={S.legend}>
          <div style={{ ...S.legendRow, color: '#999', fontSize: '0.68rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.5rem' }}>Legend</div>
          <div style={S.legendRow}><span style={{ ...S.dot, background: '#EF4444' }} /> Atlantic menhaden vessels</div>
          <div style={S.legendRow}><span style={{ ...S.dot, background: '#F59E0B' }} /> Gulf menhaden vessels</div>
          <div style={S.legendRow}><span style={{ ...S.dot, background: '#3B82F6' }} /> Herring trawlers</div>
          <div style={{ ...S.legendRow, marginTop: '0.35rem' }}>
            <span style={{ width: '9px', height: '9px', background: PLANE_COLOR, borderRadius: '2px', flexShrink: 0 }} />
            Spotter plane (inactive)
          </div>
          <div style={S.legendRow}>
            <span style={{ width: '10px', height: '10px', background: BURGUNDY, borderRadius: '2px', flexShrink: 0 }} />
            Spotter plane near vessel
          </div>
          {layers.sst && <div style={{ ...S.legendRow, marginTop: '0.35rem', color: '#888' }}>SST: blue=cold / red=warm</div>}
        </div>
      </div>

      <div style={{ display: 'flex', gap: '1.5rem', marginTop: '0.6rem' }}>
        <div style={S.sourceNote}>
          Vessels: <a href="https://globalfishingwatch.org" target="_blank" rel="noopener noreferrer" style={{ color: BURGUNDY }}>Global Fishing Watch</a> AIS
        </div>
        <div style={S.sourceNote}>
          Aircraft: <a href="https://flightaware.com/commercial/aeroapi/" target="_blank" rel="noopener noreferrer" style={{ color: BURGUNDY }}>FlightAware AeroAPI</a>
          {planeSource === 'simulated' && ' (key needed)'}
        </div>
        <div style={S.sourceNote}>
          SST: <a href="https://coastwatch.pfeg.noaa.gov/" target="_blank" rel="noopener noreferrer" style={{ color: BURGUNDY }}>NOAA CoastWatch</a> MODIS Aqua
        </div>
      </div>

      {selected && (
        <div style={S.modal} onClick={() => setSelected(null)}>
          <div style={S.modalBox} onClick={e => e.stopPropagation()}>
            <div style={S.modalHeader}>
              <div>
                <div style={{ ...S.modalTitle, color: selected.type === 'plane' ? (selected.data.isOrbiting ? BURGUNDY : PLANE_COLOR) : selected.data.groupColor }}>
                  {selected.type === 'plane' ? selected.data.ident : selected.data.name}
                </div>
                <div style={S.modalSub}>
                  {selected.type === 'plane'
                    ? `Alpha VesselCo LLC / Omega Protein · ${spotterFleet.find(p => p.ident === selected.data.ident)?.model || 'Cessna'}`
                    : selected.data.groupName
                  }
                </div>
              </div>
              <button style={S.closeBtn} onClick={() => setSelected(null)}>&times;</button>
            </div>
            <div style={S.modalBody}>
              {selected.type === 'plane' ? (
                <>
                  {selected.data.isOrbiting && (
                    <div style={{ background: '#1a0505', border: `1px solid ${BURGUNDY}`, borderLeft: `3px solid ${BURGUNDY}`, borderRadius: '4px', padding: '0.6rem 0.85rem', marginBottom: '0.85rem' }}>
                      <div style={{ fontSize: '0.72rem', color: BURGUNDY, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.2rem' }}>Active Fishing Indicator</div>
                      <div style={{ fontSize: '0.82rem', color: '#d0c0c0', lineHeight: 1.5 }}>
                        This aircraft is airborne and positioned near {selected.data.nearby.length} vessel{selected.data.nearby.length !== 1 ? 's' : ''}.
                        Spotter planes orbit over fish schools to guide vessel nets — proximity suggests active fishing operations.
                      </div>
                    </div>
                  )}
                  <div style={S.fieldLabel}>Tail Number</div>
                  <div style={S.fieldValue}>{selected.data.ident}</div>
                  {selected.data.altitude != null && <>
                    <div style={S.fieldLabel}>Altitude</div>
                    <div style={S.fieldValue}>{selected.data.altitude.toLocaleString()} ft</div>
                  </>}
                  {selected.data.groundspeed != null && <>
                    <div style={S.fieldLabel}>Ground Speed</div>
                    <div style={S.fieldValue}>{selected.data.groundspeed} kts</div>
                  </>}
                  {selected.data.heading != null && <>
                    <div style={S.fieldLabel}>Heading</div>
                    <div style={S.fieldValue}>{selected.data.heading}°</div>
                  </>}
                  {selected.data.timestamp && <>
                    <div style={S.fieldLabel}>Position As Of</div>
                    <div style={S.fieldValue}>{new Date(selected.data.timestamp).toLocaleString()}</div>
                  </>}
                  <div style={S.fieldLabel}>Registered Owner</div>
                  <div style={S.fieldValue}>Alpha VesselCo LLC (Cooke Inc. / Omega Protein)</div>
                  <div style={S.fieldLabel}>Home Base</div>
                  <div style={S.fieldValue}>Reedville, VA (Ocean Fleet Services)</div>
                  {selected.data.nearby.length > 0 && <>
                    <div style={{ ...S.fieldLabel, marginTop: '0.25rem' }}>Nearby Vessels</div>
                    {selected.data.nearby.map(v => (
                      <div key={v.mmsi} style={{ fontSize: '0.82rem', color: v.groupColor, marginBottom: '0.2rem' }}>
                        {v.name} <span style={{ color: '#888', fontSize: '0.75rem' }}>({v.groupName})</span>
                      </div>
                    ))}
                  </>}
                  {planeSource === 'simulated' && (
                    <div style={{ marginTop: '0.75rem', padding: '0.6rem', background: '#0d0d0d', borderRadius: '4px', fontSize: '0.75rem', color: '#888' }}>
                      Position simulated. Add <code>FLIGHTAWARE_API_KEY</code> to server env for live tracking.
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div style={S.fieldLabel}>MMSI</div>
                  <div style={S.fieldValue}>{selected.data.mmsi}</div>
                  {selected.data.imo && <>
                    <div style={S.fieldLabel}>IMO</div>
                    <div style={S.fieldValue}>{selected.data.imo}</div>
                  </>}
                  <div style={S.fieldLabel}>Flag</div>
                  <div style={S.fieldValue}>{selected.data.flag === 'USA' ? 'United States' : selected.data.flag}</div>
                  <div style={S.fieldLabel}>Last Seen</div>
                  <div style={S.fieldValue}>{selected.data.lastSeen?.toLocaleDateString() || 'Unknown'}</div>
                  <div style={S.fieldLabel}>Data Source</div>
                  <div style={{ ...S.fieldValue, color: selected.data.dataSource === 'gfw-api' ? GREEN : GOLD }}>
                    {selected.data.dataSource === 'gfw-api' ? '✓ Real AIS — Global Fishing Watch' : '△ Simulated position'}
                  </div>
                  <a
                    href={`https://globalfishingwatch.org/map/vessel/${selected.data.mmsi}`}
                    target="_blank" rel="noopener noreferrer"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', marginTop: '0.75rem', padding: '0.45rem 0.85rem', background: BURGUNDY, color: '#fff', borderRadius: '4px', textDecoration: 'none', fontSize: '0.8rem', fontWeight: 500 }}
                  >
                    View on GFW →
                  </a>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function fallbackVessel(vessel) {
  const positions = {
    'gulf-menhaden': { lng: -88 + Math.random() * 10, lat: 28 + Math.random() * 4 },
    'atlantic-menhaden': { lng: -76 + Math.random() * 2, lat: 36 + Math.random() * 3 },
    'atlantic-herring': { lng: -70 + Math.random() * 5, lat: 39 + Math.random() * 5 },
  }
  const pos = positions[vessel.groupId] || { lng: -75, lat: 38 }
  return { ...vessel, ...pos, lastSeen: new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000), dataSource: 'simulated' }
}

function simulatePlanePositions() {
  // Simulate ~6 active planes near the Chesapeake Bay / Reedville area
  // Fishing season is roughly May–November; outside that most planes are grounded
  const now = new Date()
  const month = now.getMonth() // 0-indexed
  const inSeason = month >= 4 && month <= 10 // May–November

  if (!inSeason) return [] // off season — no planes airborne

  return [
    { ident: 'N6216R', lat: 37.82 + (Math.random() - 0.5) * 0.3, lng: -76.01 + (Math.random() - 0.5) * 0.3, active: true, altitude: 1200, groundspeed: 95, heading: Math.floor(Math.random() * 360) },
    { ident: 'N7371',  lat: 37.55 + (Math.random() - 0.5) * 0.4, lng: -75.88 + (Math.random() - 0.5) * 0.4, active: true, altitude: 900,  groundspeed: 88, heading: Math.floor(Math.random() * 360) },
    { ident: 'N959BW', lat: 37.70 + (Math.random() - 0.5) * 0.3, lng: -76.15 + (Math.random() - 0.5) * 0.3, active: true, altitude: 1500, groundspeed: 102, heading: Math.floor(Math.random() * 360) },
    { ident: 'N268ME', lat: 37.42 + (Math.random() - 0.5) * 0.5, lng: -75.72 + (Math.random() - 0.5) * 0.5, active: false, altitude: null, groundspeed: null, heading: null },
    { ident: 'N2376R', lat: 37.89 + (Math.random() - 0.5) * 0.25, lng: -76.28 + (Math.random() - 0.5) * 0.25, active: true, altitude: 1100, groundspeed: 91, heading: Math.floor(Math.random() * 360) },
    { ident: 'N4861V', lat: 37.62 + (Math.random() - 0.5) * 0.4, lng: -75.95 + (Math.random() - 0.5) * 0.4, active: false, altitude: null, groundspeed: null, heading: null },
  ]
}
